import { alpha, createTheme, getContrastRatio, type PaletteMode, type Theme, type ThemeOptions } from '@mui/material/styles';
// Registers the x-tree-view slots (MuiTreeItem, …) on the MUI theme types so the
// hierarchy trees can be styled centrally rather than per view.
import '@mui/x-tree-view/themeAugmentation';
import '@mui/x-data-grid-premium/themeAugmentation';

export type AccentColorKey = 'navy' | 'blue' | 'teal' | 'purple' | 'orange' | 'red' | 'green';

/**
 * `main` is the light-mode brand tone (also the identity swatch in Settings). `darkMain`, when set,
 * replaces it as `palette.primary` in dark mode: the darker brand tones are illegible against the
 * NEXO_DARK backgrounds — navy especially, which is the dark shell's own hue, so primary-colored
 * controls (outlined buttons, links, tabs) would otherwise vanish. Accents without `darkMain`
 * already meet contrast on dark backgrounds and keep one tone in both modes.
 */
export const ACCENT_COLORS: Record<AccentColorKey, { main: string; darkMain?: string; secondary: string; label: string }> = {
  navy:   { main: '#1A2744', darkMain: '#8FA8DC', secondary: '#00C8B4', label: 'Navy' },   // teal accent
  blue:   { main: '#1976d2', darkMain: '#42A5F5', secondary: '#FFB300', label: 'Blue' },   // amber accent
  teal:   { main: '#00C8B4', secondary: '#FF6B6B', label: 'Teal' },                        // coral accent
  purple: { main: '#ab47bc', darkMain: '#BA68C8', secondary: '#FFD54F', label: 'Purple' }, // gold accent
  orange: { main: '#ff9800', secondary: '#00BCD4', label: 'Orange' },                      // cyan accent
  red:    { main: '#ef5350', secondary: '#26C6DA', label: 'Red' },                         // cyan accent
  green:  { main: '#66bb6a', secondary: '#FF4081', label: 'Green' },                       // pink accent
};

/* ── Nexo dark-mode palette tokens ──────────────────────────── */
/**
 * Three surface levels, not two. The old palette had only `bg` and `cardBg` about 7%
 * apart, so page, card and grid melted into one navy field; `surfaceAlt` gives raised
 * things (menus, popovers, dialogs, hovered rows) somewhere to actually sit.
 *
 * `text` is deliberately off-white rather than #FFFFFF — pure white on a near-black
 * navy halates on thin strokes and reads harsh rather than crisp.
 */
export const NEXO_DARK = {
  bg:         '#0F1524',   // page floor — slightly lifted and desaturated from #0D1123
  surface:    '#171E31',   // cards / paper
  surfaceAlt: '#1E2740',   // raised: menus, popovers, dialogs, hovered rows
  accent:     '#00C8B4',   // teal/cyan accent
  accentAlt:  '#00D2AA',   // lighter teal for subtitles
  text:       '#E4E8F2',
  textMuted:  '#98A2BC',   // a real step below `text`, so hierarchy is readable
  divider:    'rgba(148, 163, 196, 0.18)',
};

/** Light-mode surfaces and text the built-in accents share. The text values are MUI's own defaults. */
const NEXO_LIGHT = {
  background:    '#F8F9F9',
  surface:       '#ffffff',
  surfaceRaised: '#ffffff',
  text:          'rgba(0, 0, 0, 0.87)',
  textSecondary: 'rgba(0, 0, 0, 0.6)',
};

/** The accent preference value that wears the tenant's own colours instead of a built-in pair. */
export const CUSTOM_ACCENT = 'custom';
export type AccentChoice = AccentColorKey | typeof CUSTOM_ACCENT;

/**
 * Every colour the theme takes, for one mode. A built-in accent fills it from its pair and the
 * shared surfaces; the tenant's own colours set each one.
 */
export interface ThemeColors {
  primary: string;
  secondary: string;
  background: string;
  surface: string;
  surfaceRaised: string;
  text: string;
  textSecondary: string;
}

export type ThemeColorKey = keyof ThemeColors;

export const THEME_COLOR_KEYS: ThemeColorKey[] = [
  'primary', 'secondary', 'background', 'surface', 'surfaceRaised', 'text', 'textSecondary',
];

/** The tenant's own colours, one palette per mode — the shape `/api/tenant-theme` carries. */
export interface CustomTheme {
  light: ThemeColors;
  dark: ThemeColors;
}

export function builtInThemeColors(accentColor: AccentColorKey, mode: PaletteMode): ThemeColors {
  const accent = ACCENT_COLORS[accentColor] ?? ACCENT_COLORS.navy;
  if (mode === 'light') {
    return { primary: accent.main, secondary: accent.secondary, ...NEXO_LIGHT };
  }
  return {
    primary: accent.darkMain ?? accent.main,
    secondary: accent.secondary,
    background: NEXO_DARK.bg,
    surface: NEXO_DARK.surface,
    surfaceRaised: NEXO_DARK.surfaceAlt,
    text: NEXO_DARK.text,
    textSecondary: NEXO_DARK.textMuted,
  };
}

/**
 * The colours a user wears: the tenant's own for "custom" while the tenant has them, otherwise the
 * built-in accent — navy for a value that names none, "custom" without colours included.
 */
export function resolveThemeColors(
  mode: PaletteMode,
  accentColor: string,
  customTheme: CustomTheme | null | undefined,
): ThemeColors {
  if (accentColor === CUSTOM_ACCENT && customTheme) return customTheme[mode];
  return builtInThemeColors(accentColor as AccentColorKey, mode);
}

/**
 * Where the editor starts when the tenant has no colours yet: navy in both modes. The editor takes
 * `#RRGGBB` only, so the light text is the hex MUI's translucent defaults come to on white.
 */
export const DEFAULT_CUSTOM_THEME: CustomTheme = {
  light: { ...builtInThemeColors('navy', 'light'), text: '#212121', textSecondary: '#666666' },
  dark: builtInThemeColors('navy', 'dark'),
};

export function isHexColor(value: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(value);
}

export type ContrastCheck =
  | 'textOnBackground'
  | 'textOnSurface'
  | 'textOnSurfaceRaised'
  | 'textSecondaryOnSurface'
  | 'primaryOnSurface';

export interface ContrastWarning {
  check: ContrastCheck;
  ratio: number;
  minimum: number;
}

/** WCAG AA: 4.5:1 for body text, 3:1 for buttons, links and other controls. */
const CONTRAST_CHECKS: { check: ContrastCheck; foreground: ThemeColorKey; background: ThemeColorKey; minimum: number }[] = [
  { check: 'textOnBackground', foreground: 'text', background: 'background', minimum: 4.5 },
  { check: 'textOnSurface', foreground: 'text', background: 'surface', minimum: 4.5 },
  { check: 'textOnSurfaceRaised', foreground: 'text', background: 'surfaceRaised', minimum: 4.5 },
  { check: 'textSecondaryOnSurface', foreground: 'textSecondary', background: 'surface', minimum: 4.5 },
  { check: 'primaryOnSurface', foreground: 'primary', background: 'surface', minimum: 3 },
];

/** The pairs in a palette that fall below their WCAG AA minimum. Every colour must be valid. */
export function contrastWarnings(colors: ThemeColors): ContrastWarning[] {
  return CONTRAST_CHECKS
    .map(({ check, foreground, background, minimum }) => ({
      check,
      minimum,
      ratio: getContrastRatio(colors[foreground], colors[background]),
    }))
    .filter((warning) => warning.ratio < warning.minimum);
}

const sharedTypography = {
  h4: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.2 },
  h5: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.3 },
  h6: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.4 },
  subtitle1: { fontSize: '0.9375rem', fontWeight: 500, lineHeight: 1.5 },
  subtitle2: { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.5 },
  body1: { fontSize: '0.875rem', lineHeight: 1.5 },
  body2: { fontSize: '0.8125rem', lineHeight: 1.5 },
};

const sharedComponents: ThemeOptions['components'] = {
  MuiFormControl: { defaultProps: { size: 'small' as const, margin: 'dense' as const } },
  MuiTextField: { defaultProps: { size: 'small' as const, margin: 'dense' as const } },
  MuiSelect: { defaultProps: { size: 'small' as const } },
  // The grid's edit cells wrap their select in a FormControl (MUI X BaseSelect), which would take the dense margin above
  // and push the value out of the row; in a cell a control has no margin. The wrapper also renders an empty label, and
  // MUI leaves room under a label for the input (`label + .MuiInput-root`), which would push it out of the row too.
  MuiDataGrid: {
    styleOverrides: {
      root: {
        '& .MuiDataGrid-cell .MuiFormControl-root': { margin: 0 },
        '& .MuiDataGrid-cell label + .MuiInputBase-root': { marginTop: 0 },
      },
    },
  },
  MuiList: { styleOverrides: { root: { paddingTop: 4, paddingBottom: 4 } } },
  MuiListItem: { styleOverrides: { root: { paddingTop: 4, paddingBottom: 4 } } },
  MuiListItemText: {
    styleOverrides: {
      primary: { fontSize: '0.875rem' },
      secondary: { fontSize: '0.75rem' },
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: { minWidth: 36, marginRight: 8 },
    },
  },
  MuiAutocomplete: {
    styleOverrides: {
      // Popper.js stamps this attribute when the popup's anchor is scrolled fully out of
      // its clipping container (e.g. a grid cell in edit mode leaving the virtual
      // scroller). The popup is portaled to <body>, so without this it keeps floating
      // over unrelated content below the grid.
      popper: {
        '&[data-popper-reference-hidden]': {
          visibility: 'hidden',
          pointerEvents: 'none',
        },
      },
    },
  },
  /**
   * Hierarchy trees (org units, account groups, product groups, rollup node picker).
   * Every one of them is a bare `RichTreeView` that styles only its own outer padding,
   * and two of the four live in `.generated.tsx` files — so the shared look has to come
   * from the theme, not from per-view `sx`.
   *
   * x-tree-view gives the label `typography.body1` (0.875rem here), which left the trees
   * a step larger than the rest of the dense UI, and pairs it with a full-size checkbox
   * at `action.active` — heavier than the label text it sits next to.
   */
  MuiTreeItem: {
    styleOverrides: {
      // Only the block padding: `paddingLeft` carries the depth-indentation calc.
      content: { paddingTop: 3, paddingBottom: 3, minHeight: 28 },
      label: { fontSize: '0.8125rem' },
      // x-tree-view already zeroes the checkbox padding, so the 24px glyph *is* the
      // row height driver; shrinking it is what lets the row tighten.
      checkbox: ({ theme }) => ({
        color: theme.palette.text.disabled,
        '& .MuiSvgIcon-root': { fontSize: 18 },
      }),
    },
  },
};

/**
 * `locales`: theme overrides that translate MUI components for the active language
 * (`dataGridLocale`), merged over the app theme.
 */
export function createAppTheme(
  mode: PaletteMode,
  colors: ThemeColors = builtInThemeColors('navy', mode),
  locales: object[] = [],
): Theme {
  return createTheme({
    palette: {
      mode,
      primary: { main: colors.primary },
      // The accent's paired second color (navy → teal, blue → amber, …) — never a hardcoded hue,
      // so everything using palette.secondary follows the chosen theme.
      secondary: { main: colors.secondary },
      background: { default: colors.background, paper: colors.surface },
      text: { primary: colors.text, secondary: colors.textSecondary },
      ...(mode === 'dark'
        ? {
            divider: NEXO_DARK.divider,
            // Hover and selection carry the user's accent instead of MUI's flat white
            // wash. This is what puts colour back into the shell: grid rows, list items,
            // tabs and tree nodes all route through action.hover/selected.
            action: {
              hover: alpha(colors.secondary, 0.08),
              hoverOpacity: 0.08,
              selected: alpha(colors.secondary, 0.14),
              selectedOpacity: 0.14,
            },
          }
        : {}),
    },
    typography: sharedTypography,
    components: {
      ...sharedComponents,
      MuiCard: {
        styleOverrides: {
          root: mode === 'dark' ? { backgroundImage: 'none' } : {},
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderBottom: '1px solid',
            borderColor: mode === 'dark' ? NEXO_DARK.divider : 'rgba(0,0,0,0.08)',
            ...(mode === 'dark' ? { backgroundImage: 'none', backgroundColor: colors.background } : {}),
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          // MUI normally fakes dark elevation with a white gradient overlay, which is
          // switched off here because it greys out the navy. With it off, every raised
          // paper rendered at card colour — that flatness is why menus, popovers and the
          // mega menu had no separation from the page. Give them the real raised surface.
          // Light mode takes it too: the built-in light surfaces are both white, but the
          // tenant's own colours may set them apart.
          root: ({ ownerState }) => ({
            ...(mode === 'dark' ? { backgroundImage: 'none' } : {}),
            ...(ownerState.variant === 'elevation' &&
              (ownerState.elevation ?? 1) >= 4 && {
                backgroundColor: colors.surfaceRaised,
              }),
          }),
        },
      },
    },
  }, ...locales);
}

/** Default theme export for backward compatibility */
export const theme = createAppTheme('light');
